import DeleteIcon from "@mui/icons-material/Delete";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import { Box, Chip, IconButton, Tooltip, useTheme } from "@mui/material";
import { useCallback } from "react";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type { PauseSegmentFE } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { useScriptEditor } from "Projects/Components/ScriptEditor/ScriptEditorContext";
import {
  getSegmentErrors,
  type ScriptValidationError,
} from "Projects/Utils/validateScript";

interface PauseSegmentNodeProps {
  index: number;
  segment: PauseSegmentFE;
  validationErrors?: ScriptValidationError[];
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, "0")}`
    : `${seconds}s`;
}

export const PauseSegmentNode = ({
  index,
  segment,
  validationErrors = [],
}: PauseSegmentNodeProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const { removeSegment, selectedSegmentIndex, focusSegment, isEditable } =
    useScriptEditor();

  const isSelected = selectedSegmentIndex === index;
  const segErrors = getSegmentErrors(validationErrors, index);
  const hasError = segErrors.length > 0;

  const chipColor = hasError
    ? theme.palette.error.main
    : theme.palette.info.main;
  const errorTooltip = hasError
    ? t(Resources.JobDetail.SegmentZeroDuration)
    : undefined;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      focusSegment(index);
    },
    [index, focusSegment],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeSegment(index);
    },
    [index, removeSegment],
  );

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        position: "relative",
        m: 0.5,
        verticalAlign: "middle",
        "&:hover .pause-delete-btn": { opacity: 1 },
      }}
    >
      <Tooltip title={errorTooltip} placement="top">
        <Chip
          icon={<PauseCircleIcon sx={{ fontSize: 14 }} />}
          label={formatDuration(segment.durationMs)}
          size="small"
          sx={{
            fontSize: "12px",
            height: 26,
            backgroundColor: isSelected ? `${chipColor}33` : `${chipColor}22`,
            color: chipColor,
            border: `1px solid ${isSelected ? chipColor : `${chipColor}66`}`,
            cursor: "pointer",
            "& .MuiChip-icon": { color: chipColor },
            transition: "background-color 150ms, border-color 150ms",
            "&:hover": {
              backgroundColor: `${chipColor}33`,
            },
          }}
        />
      </Tooltip>
      {isEditable && (
        <Tooltip title={t(Resources.JobDetail.DeletePause)} placement="top">
          <IconButton
            className="pause-delete-btn"
            size="small"
            onClick={handleRemove}
            sx={{
              ml: 0.25,
              opacity: 0,
              p: 0.25,
              color: "text.secondary",
              transition: "opacity 150ms",
              "&:hover": { color: "error.main" },
            }}
          >
            <DeleteIcon sx={{ fontSize: 11 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
